import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { buildProvisioningPayload } from "../../services/provisioningQR";
import IconButton from "./IconButton";
import CloseIcon from "@mui/icons-material/Close";

type Props = {
  open: boolean;
  onClose: () => void;
  sensorName: string;
  displayName?: string;
};

export default function ProvisioningQRModal({
  open,
  onClose,
  sensorName,
  displayName,
}: Props) {
  const [payload, setPayload] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Recompute the payload whenever the modal opens for a (potentially) new sensor.
  useEffect(() => {
    if (!open || !sensorName) return;
    let cancelled = false;
    setPayload(null);
    setError(null);
    buildProvisioningPayload(sensorName)
      .then((p) => {
        if (!cancelled) setPayload(p);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to build provisioning payload", err);
        if (!cancelled) setError("Could not build the provisioning payload.");
      });
    return () => {
      cancelled = true;
    };
  }, [open, sensorName]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Provisioning QR Code"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-card bg-white text-ink p-6 shadow-depth border border-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Provisioning QR Code</h2>
          <IconButton
            label="Close"
            onClick={onClose}
            className="!w-8 !h-8 !bg-black/[0.04] !text-ink !border-black/10 hover:!bg-black/10"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className="flex flex-col items-center gap-4">
          {error ? (
            <div className="text-alert-critical text-sm py-12">{error}</div>
          ) : payload ? (
            <div className="bg-white p-3 rounded-md border border-black/10">
              <QRCodeSVG
                value={payload}
                size={280}
                level="H"
                marginSize={2}
              />
            </div>
          ) : (
            <div className="h-[296px] w-[296px] flex items-center justify-center text-ink-3 text-sm">
              Generating…
            </div>
          )}
          <div className="text-center">
            {displayName && (
              <div className="text-lg font-semibold">{displayName}</div>
            )}
            <div className="text-ink-3 text-xs font-mono mt-1 break-all">
              {sensorName}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
